import { strings } from "../../../resources/strings";
import {
  isNonNumericName,
  isValidEmail,
  isValidVehicleYear,
} from "../../../utils/inputValidation";

const errs = strings.reportForm.workflow.errors;

/**
 * Per-field format validation for the Detailed Information workflow forms
 * (`StructuredForm`, `RepeatableListForm`, `ConfirmOnFileForm`). Keyed by the
 * wire `fieldKey` (from {@link WORKFLOW_REGISTRY}) — each key used here is
 * unique to a single workflow (e.g. `year`/`ownerName` only appear on
 * `vehicles`), so no `workflowName` disambiguation is needed.
 *
 * Also carries the native `type`/`inputMode` hint so the right on-screen
 * keyboard shows on iPad/mobile (e.g. numeric for a year).
 */
interface FieldRule {
  test: (value: string) => boolean;
  message: string;
  type?: "text" | "email";
  inputMode?: "numeric" | "email";
}

const FIELD_RULES: Record<string, FieldRule> = {
  email: { test: isValidEmail, message: errs.email, type: "email", inputMode: "email" },
  year: {
    test: isValidVehicleYear,
    message: errs.vehicleYear,
    inputMode: "numeric",
  },
  ownerName: { test: isNonNumericName, message: errs.ownerName },
};

/** The native `type`/`inputMode` hint for a field, or `undefined` for a plain text field. */
export const getFieldInputHints = (
  fieldKey: string,
): Pick<FieldRule, "type" | "inputMode"> => {
  const rule = FIELD_RULES[fieldKey];
  return { type: rule?.type, inputMode: rule?.inputMode };
};

/**
 * Format-validation error for a field's current value, or `undefined` when
 * the value is blank (required-ness is gated separately) or matches the
 * field's expected format (or the field has no registered rule).
 */
export const getFieldError = (
  fieldKey: string,
  value: string,
): string | undefined => {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return undefined;
  }
  const rule = FIELD_RULES[fieldKey];
  if (!rule) {
    return undefined;
  }
  return rule.test(trimmed) ? undefined : rule.message;
};
